import type { Fill, Worksheet } from 'exceljs';
import { toSpreadsheetSheetName } from '@erp/shared-utils';

import type { NameMatchLevel } from './matchCustomerNames';
import type {
  CustomerSpa,
  CustomerSpaStatus,
  DivisionSpa,
  UnloadedSpaReport,
} from './unloadedSpaReport';

/**
 * The report as the pricing team asked for it: the customers' missing SPAs
 * first, red where the SPA is loaded for nobody in the Division and amber
 * where it is loaded but not on the customer's account; what is already
 * loaded and what has no SPA on separate sheets, so the first sheet holds
 * only work to do. Then the Division-wide lists and a Summary.
 *
 * The copy is passed in translated (the writer has no translator), the way
 * the CSV comparison export does it. ExcelJS is loaded on demand.
 */

export interface UnloadedSpaWorkbookLabels {
  sheets: {
    customersNotLoaded: string;
    customersLoaded: string;
    customersWithoutSpa: string;
    divisionNotLoaded: string;
    loadedGroups: string;
    otherDivision: string;
    summary: string;
  };
  columns: {
    customerName: string;
    customerNumber: string;
    status: string;
    meaning: string;
    alsoLoaded: string;
    quote: string;
    endCustomerName: string;
    endCustomerNumber: string;
    nameMatch: string;
    spaType: string;
    endCustomerCount: string;
    branches: string;
    quoteDate: string;
    validFrom: string;
    validTo: string;
    revision: string;
    loadedAccounts: string;
    loadedDivisions: string;
    yourCustomer: string;
    note: string;
    item: string;
    value: string;
  };
  status: Record<CustomerSpaStatus, string>;
  match: Record<NameMatchLevel, string>;
  singleCustomer: string;
  group: string;
  yes: string;
  no: string;
  allBranches: (count: number) => string;
  meaning: (spa: {
    single: boolean;
    status: CustomerSpaStatus;
    count: number;
  }) => string;
  noSpa: string;
  elsewhere: (name: string, quotes: string, divisions: string) => string;
  otherDivisionNote: string;
  summary: {
    spaList: string;
    results: string;
    division: string;
    branches: string;
    spas: string;
    loaded: string;
    notLoaded: string;
    otherDivision: string;
    customers: string;
    customersMissing: string;
    customersWithoutSpa: string;
    caveat: string;
    caveatText: string;
  };
}

export interface UnloadedSpaWorkbookInput {
  report: UnloadedSpaReport;
  /** The Division as the pricing team names it ("N. Ontario (R)"). */
  divisionName: string;
  spaListFileName: string;
  resultsFileName: string;
  /** False without a customer list: only the Division-wide sheets are written. */
  withCustomers: boolean;
  labels: UnloadedSpaWorkbookLabels;
}

function solid(argb: string): Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

const FILLS = {
  header: solid('FF1F4E78'),
  notLoaded: solid('FFF8CBAD'),
  missingForAccount: solid('FFFFE699'),
} as const;

type CellValue = string | number;

interface Column<Row> {
  header: string;
  value: (row: Row) => CellValue;
}

/** Wide enough for its longest value among the first rows, within reason. */
function width(values: readonly CellValue[]): number {
  const longest = Math.max(...values.map((value) => String(value).length));
  return Math.min(Math.max(10, longest + 2), 60);
}

function writeTable<Row>(
  sheet: Worksheet,
  columns: readonly Column<Row>[],
  rows: readonly Row[],
  fill?: (row: Row) => Fill | undefined,
): void {
  sheet.addRow(columns.map((column) => column.header));
  for (const row of rows) {
    const added = sheet.addRow(columns.map((column) => column.value(row)));
    const rowFill = fill?.(row);
    if (rowFill) {
      for (let index = 1; index <= columns.length; index += 1) {
        added.getCell(index).fill = rowFill;
      }
    }
  }
  const header = sheet.getRow(1);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.alignment = { wrapText: true, vertical: 'top' };
  for (let index = 1; index <= columns.length; index += 1) {
    header.getCell(index).fill = FILLS.header;
  }
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  if (rows.length > 0) {
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: rows.length + 1, column: columns.length },
    };
  }
  columns.forEach((column, index) => {
    const sample = rows.slice(0, 500).map(column.value);
    sheet.getColumn(index + 1).width = width([column.header, ...sample]);
  });
}

function spaColumns(
  labels: UnloadedSpaWorkbookLabels,
): Record<string, Column<DivisionSpa>> {
  const { columns } = labels;
  return {
    quote: { header: columns.quote, value: (spa) => spa.quote },
    endCustomerName: {
      header: columns.endCustomerName,
      value: (spa) => spa.endCustomerName,
    },
    endCustomerNumber: {
      header: columns.endCustomerNumber,
      value: (spa) => spa.endCustomerNumber,
    },
    spaType: {
      header: columns.spaType,
      value: (spa) =>
        spa.endCustomerCount === 1 ? labels.singleCustomer : labels.group,
    },
    endCustomerCount: {
      header: columns.endCustomerCount,
      value: (spa) => spa.endCustomerCount,
    },
    branches: {
      header: columns.branches,
      value: (spa) =>
        spa.allBranches
          ? labels.allBranches(spa.branches.length)
          : spa.branches.join(', '),
    },
    quoteDate: { header: columns.quoteDate, value: (spa) => spa.quoteDate },
    validFrom: { header: columns.validFrom, value: (spa) => spa.validFrom },
    validTo: { header: columns.validTo, value: (spa) => spa.validTo },
    revision: { header: columns.revision, value: (spa) => spa.revision },
    loadedAccounts: {
      header: columns.loadedAccounts,
      value: (spa) => spa.loadedAccounts.join('; '),
    },
    loadedDivisions: {
      header: columns.loadedDivisions,
      value: (spa) => spa.loadedDivisions.join(', '),
    },
  };
}

function customerColumns(
  labels: UnloadedSpaWorkbookLabels,
  alsoLoaded: ReadonlySet<string>,
  withStatus: boolean,
): Column<CustomerSpa>[] {
  const spa = spaColumns(labels);
  const { columns } = labels;
  const of = (column: Column<DivisionSpa>): Column<CustomerSpa> => ({
    header: column.header,
    value: (row) => column.value(row.spa),
  });
  const status: Column<CustomerSpa>[] = withStatus
    ? [
        { header: columns.status, value: (row) => labels.status[row.status] },
        {
          header: columns.meaning,
          value: (row) =>
            labels.meaning({
              single: row.spa.endCustomerCount === 1,
              status: row.status,
              count: row.spa.endCustomerCount,
            }),
        },
        {
          header: columns.alsoLoaded,
          value: (row) =>
            alsoLoaded.has(row.customer.number) ? labels.yes : labels.no,
        },
      ]
    : [];
  return [
    { header: columns.customerName, value: (row) => row.customer.name },
    { header: columns.customerNumber, value: (row) => row.customer.number },
    ...status,
    of(spa.quote),
    of(spa.endCustomerName),
    of(spa.endCustomerNumber),
    { header: columns.nameMatch, value: (row) => labels.match[row.match] },
    of(spa.spaType),
    of(spa.endCustomerCount),
    of(spa.branches),
    of(spa.validFrom),
    of(spa.validTo),
    of(spa.revision),
    of(spa.loadedAccounts),
  ];
}

function writeCustomerSheets(
  add: (name: string) => Worksheet,
  input: UnloadedSpaWorkbookInput,
): void {
  const { report, labels } = input;
  const missing = report.customerSpas.filter(
    (row) =>
      row.status === 'not loaded' || row.status === 'missing for account',
  );
  const loaded = report.customerSpas.filter((row) => row.status === 'loaded');
  const alsoLoaded = new Set(loaded.map((row) => row.customer.number));
  writeTable(
    add(labels.sheets.customersNotLoaded),
    customerColumns(labels, alsoLoaded, true),
    missing,
    (row) =>
      row.status === 'not loaded' ? FILLS.notLoaded : FILLS.missingForAccount,
  );
  writeTable(
    add(labels.sheets.customersLoaded),
    customerColumns(labels, alsoLoaded, false),
    loaded,
  );
  writeTable(
    add(labels.sheets.customersWithoutSpa),
    [
      {
        header: labels.columns.customerName,
        value: (row) => row.customer.name,
      },
      {
        header: labels.columns.customerNumber,
        value: (row) => row.customer.number,
      },
      {
        header: labels.columns.note,
        value: (row) =>
          row.elsewhere
            ? labels.elsewhere(
                row.elsewhere.endCustomerName,
                row.elsewhere.quotes.join(', '),
                row.elsewhere.divisions.join(', '),
              )
            : labels.noSpa,
      },
    ],
    report.customersWithoutSpa,
  );
}

function writeDivisionSheets(
  add: (name: string) => Worksheet,
  input: UnloadedSpaWorkbookInput,
): void {
  const { report, labels } = input;
  const spa = spaColumns(labels);
  writeTable(
    add(labels.sheets.divisionNotLoaded),
    [
      spa.quote,
      spa.endCustomerNumber,
      spa.endCustomerName,
      spa.branches,
      spa.quoteDate,
      spa.validFrom,
      spa.validTo,
      spa.revision,
      spa.endCustomerCount,
      spa.spaType,
    ],
    // Groups first, as the team reads them: one SPA, many customers.
    [...report.notLoaded].sort(
      (a, b) =>
        Number(a.endCustomerCount === 1) - Number(b.endCustomerCount === 1),
    ),
  );
  writeTable(
    add(labels.sheets.loadedGroups),
    [
      spa.quote,
      spa.endCustomerNumber,
      spa.endCustomerName,
      spa.branches,
      spa.validFrom,
      spa.validTo,
      spa.revision,
      spa.endCustomerCount,
      spa.loadedAccounts,
    ],
    [...report.loadedGroups].sort(
      (a, b) =>
        a.quote.localeCompare(b.quote) ||
        a.endCustomerName.localeCompare(b.endCustomerName),
    ),
  );
  // Several listed customers may match one end customer: keep them all.
  const yourCustomers = new Map<string, Set<string>>();
  for (const row of report.customerSpas) {
    if (row.status !== 'other division') continue;
    const key = `${row.spa.quote}\u0000${row.spa.endCustomerNumber}`;
    const names = yourCustomers.get(key) ?? new Set<string>();
    yourCustomers.set(key, names);
    names.add(`${row.customer.name} (${row.customer.number})`);
  }
  writeTable(
    add(labels.sheets.otherDivision),
    [
      { header: labels.columns.note, value: () => labels.otherDivisionNote },
      ...(input.withCustomers
        ? [
            {
              header: labels.columns.yourCustomer,
              value: (row: DivisionSpa) =>
                [
                  ...(yourCustomers.get(
                    `${row.quote}\u0000${row.endCustomerNumber}`,
                  ) ?? []),
                ].join('; '),
            },
          ]
        : []),
      spa.quote,
      spa.endCustomerName,
      spa.endCustomerNumber,
      spa.loadedDivisions,
      spa.branches,
      spa.validFrom,
      spa.validTo,
      spa.endCustomerCount,
    ],
    report.otherDivision,
  );
}

function writeSummary(sheet: Worksheet, input: UnloadedSpaWorkbookInput): void {
  const { report, labels } = input;
  const { summary } = labels;
  const quotes = (spas: readonly DivisionSpa[]) =>
    new Set(spas.map((spa) => spa.quote)).size;
  const rows: [string, CellValue][] = [
    [summary.spaList, input.spaListFileName],
    [summary.results, input.resultsFileName],
    [summary.division, input.divisionName],
    [summary.branches, report.branches.length],
    [summary.spas, report.spaCount],
    [summary.loaded, report.loadedSpaCount],
    [summary.notLoaded, quotes(report.notLoaded)],
    [summary.otherDivision, quotes(report.otherDivision)],
  ];
  if (input.withCustomers) {
    const missing = report.customerSpas.filter(
      (row) =>
        row.status === 'not loaded' || row.status === 'missing for account',
    );
    rows.push(
      [
        summary.customers,
        report.customersWithoutSpa.length +
          new Set(report.customerSpas.map((row) => row.customer.number)).size,
      ],
      [
        summary.customersMissing,
        new Set(missing.map((row) => row.customer.number)).size,
      ],
      [summary.customersWithoutSpa, report.customersWithoutSpa.length],
      [summary.caveat, summary.caveatText],
    );
  }
  writeTable(
    sheet,
    [
      { header: labels.columns.item, value: (row) => row[0] },
      { header: labels.columns.value, value: (row) => row[1] },
    ],
    rows,
  );
  sheet.getColumn(2).alignment = { wrapText: true, vertical: 'top' };
}

export async function writeUnloadedSpaWorkbook(
  input: UnloadedSpaWorkbookInput,
): Promise<ArrayBuffer> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const add = (name: string) =>
    workbook.addWorksheet(toSpreadsheetSheetName(name, 'Sheet'));
  if (input.withCustomers) writeCustomerSheets(add, input);
  writeDivisionSheets(add, input);
  writeSummary(add(input.labels.sheets.summary), input);
  return workbook.xlsx.writeBuffer();
}
